import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { Text } from '@coreai/fluentui-react';
import { useMonitoringEmptyStateStyles } from './MonitoringEmptyState.styles';

type MonitoringEmptyStateProps = {
  /** Icon element. The shared icon style class is merged into its className for consistent sizing/color. */
  icon: ReactElement<{ className?: string }>;
  title: string;
  subtitle?: ReactNode;
  /** Optional CTA slot (typically a Fluent Button). */
  actions?: ReactNode;
};

/**
 * Card-style empty state used across monitoring surfaces. Keeps the visual
 * (icon halo + title + subtitle + action) consistent so users see the same
 * affordance whether a runs list or a trigger history list is empty.
 */
export const MonitoringEmptyState = ({
  icon,
  title,
  subtitle,
  actions,
}: MonitoringEmptyStateProps) => {
  const styles = useMonitoringEmptyStateStyles();

  // Merge our icon class into whatever the caller already had so we don't
  // clobber custom styling but still apply the canonical size/color.
  const iconWithClass = isValidElement(icon)
    ? cloneElement(icon, {
        className: [icon.props.className, styles.icon].filter(Boolean).join(' '),
      })
    : icon;

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.iconContainer}>{iconWithClass}</div>
        <Text className={styles.title}>{title}</Text>
        {subtitle && <Text className={styles.subtitle}>{subtitle}</Text>}
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
    </div>
  );
};
